// frontend/src/api/emailApi.js
// Updated: April 10, 2025
// Purpose: API functions specifically for email operations

import { apiClient } from './index';

/**
 * Helper function to handle API responses
 * @param {Object} response - The axios response
 * @returns {Object} Processed response
 */
const handleResponse = (response) => {
  // If the response contains a 'leads' property, preserve it
  if (response.data.leads) {
    return {
      success: true,
      leads: response.data.leads,
      message: response.data.message
    };
  }
  
  // Otherwise fall back to the default data handling
  return {
    success: true,
    data: response.data.data || response.data,
    message: response.data.message
  };
};

/**
 * Handle error responses
 * @param {Error} error - The error object
 * @returns {Object} Error response
 */
const handleError = (error) => {
  console.error('API Error:', {
    status: error.response?.status,
    statusText: error.response?.statusText,
    data: error.response?.data,
    message: error.message,
    config: {
      url: error.config?.url,
      method: error.config?.method,
      params: error.config?.params
    }
  });
  
  return {
    success: false,
    message: error.response?.data?.message || error.message || 'An error occurred. Please try again.'
  };
};

/**
 * Send an initial lead notification email to a specific agent
 * This email hides contact details to encourage lead purchase
 * @param {number} leadId ID of the lead
 * @param {string} agentEmail Email address of the agent
 * @returns {Promise} API response
 */
export const sendInitialLeadEmail = async (leadId, agentEmail) => {
  try {
    console.log('Sending initial email for lead', leadId, 'to', agentEmail);
    // Use the correct path to the email controller endpoint
    const response = await apiClient.post('/admin/email/send-initial', { leadId, agentEmail });
    return handleResponse(response);
  } catch (error) {
    console.error('Error sending initial email:', error);
    return handleError(error);
  }
};

/**
 * Send full lead details to a specific agent
 * This email includes all contact information and is typically sent after purchase
 * @param {number} leadId ID of the lead
 * @param {string} agentEmail Email address of the agent
 * @returns {Promise} API response
 */
export const sendFullLeadEmail = async (leadId, agentEmail) => {
  try {
    // Use the correct path to the email controller endpoint
    const response = await apiClient.post('/admin/email/send-full', { leadId, agentEmail });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Notify all agents in a lead's state about the new lead
 * @param {number} leadId ID of the lead
 * @param {string} state State code (e.g., 'FL')
 * @returns {Promise} API response
 */
export const notifyAllAgentsInState = async (leadId, state) => {
  try {
    console.log(`Notifying agents in state ${state} about lead ${leadId}`);
    // Use the correct path to the email controller endpoint
    const response = await apiClient.post('/admin/email/notify-state', { 
      leadId, 
      state 
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Error notifying agents:', error);
    return handleError(error);
  }
};
  
/**
 * Get email history for a specific lead
 * @param {number} leadId ID of the lead
 * @returns {Promise} API response with email history
 */
export const getLeadEmailHistory = async (leadId) => {
  try {
    // Use the correct path to the email history endpoint
    const response = await apiClient.get(`/admin/email/history/${leadId}`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};
  
/**
 * Get agents by state
 * Returns a list of agents who have the specified state in their selected states
 * @param {string} state - The state code to filter by (e.g., 'CA', 'NY')
 * @returns {Promise} API response
 */
export const getAgentsByState = async (state) => {
  try {
    // Fix: Using the correct path - this is crucial!
    const response = await apiClient.get(`/admin/agents/by-state/${state}`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};
  
/**
 * Get preview data for email templates
 * Returns the rendered HTML for different email types with sample data
 * @param {string} emailType - The type of email template ('initial', 'full')
 * @param {number} leadId - Optional lead ID to use real data instead of sample data
 * @returns {Promise} API response with rendered HTML
 */
export const getEmailPreview = async (emailType, leadId = null) => {
  try {
    console.log('Getting email preview for type:', emailType, 'leadId:', leadId);
    // Fix: Use the correct path to the email preview endpoint
    const url = `/admin/email/preview/${emailType}${leadId ? `?leadId=${leadId}` : ''}`;
    
    const response = await apiClient.get(url);
    console.log('Email preview response:', response.data);
    return handleResponse(response);
  } catch (error) {
    console.error('Error getting email preview:', error);
    return handleError(error);
  }
};
  
/**
 * Resend emails to previously notified agents
 * @param {number} leadId - The ID of the lead
 * @returns {Promise} API response
 */
export const resendLeadEmails = async (leadId) => {
  try {
    // Fix: Use the correct path to the resend endpoint
    const response = await apiClient.post(`/admin/email/resend/${leadId}`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};
  
/**
 * Get email statistics
 * @returns {Promise} API response with email statistics
 */
export const getEmailStats = async () => {
  try {
    // Fix: Use the correct path to the email stats endpoint
    const response = await apiClient.get('/admin/email/stats');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};
  
/**
 * Check resend status
 * @returns {Promise} API response with resend status
 */
export const checkResendStatus = async () => {
  try {
    // Fix: Use the correct path to the email status endpoint
    const response = await apiClient.get('/admin/email/status');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

// Export all functions as a default object
const emailApi = {
  sendInitialLeadEmail,
  sendFullLeadEmail,
  notifyAllAgentsInState,
  getLeadEmailHistory,
  getAgentsByState,
  getEmailPreview,
  resendLeadEmails,
  getEmailStats,
  checkResendStatus
};

export default emailApi;